import { Component, ElementRef, ViewChild, OnInit, AfterViewInit, effect } from '@angular/core';
import { SchedulesHelperService } from '../../../pages/schedules/schedules-helper.service';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { HelperServiceService } from '../../../utils/helper-service.service';
import { MomentjsPipe } from '../../../utils/momentjs.pipe';
import { SchedulesApiService } from '../../../pages/schedules/schedules-api.service';
import { debounce } from 'lodash';
import { CommonStaffApiServiceService } from '../../../pages/personnel/common-staff-api-service.service';
import { Observable } from 'rxjs';
import { CascadingSelectServiceService } from '../../../pages/services/cascading-select-service.service';
import { departments } from '../../../providers/static-data';
import { ToasterService } from '../../../providers/toaster/toaster.service';


@Component({
  selector: 'app-schedule-form',
  standalone: true,
  imports: [CommonModule, FormsModule, FormsModule, ReactiveFormsModule],
  templateUrl: './schedule-form.component.html',
  styleUrl: './schedule-form.component.css'
})
export class ScheduleFormComponent implements OnInit, AfterViewInit {
  eventId: any
  actionType: any
  schedulerForm!: FormGroup
  isFormInitialized = false;
  private lastLoadedEventId: string | number | null = null;
  @ViewChild('additionalParamsInput') additionalParamsInput!: ElementRef<HTMLInputElement>;
  alltimeOptions: string[] = [];
  totimeOptions: string[] = [];



  weekDays: { id: number, name: string, initials: string }[] = [
    { id: 1, name: 'Monday', initials: 'MON' },
    { id: 2, name: 'Tuesday', initials: 'TUE' },
    { id: 3, name: 'Wednesday', initials: 'WED' },
    { id: 4, name: 'Thursday', initials: 'THU' },
    { id: 5, name: 'Friday', initials: 'FRI' },
    { id: 6, name: 'Saturday', initials: 'SAT' },
    { id: 7, name: 'Sunday', initials: 'SUN' }
  ];


  courses$:Observable<any>

  users: any[] = []
  selectedUsers: any[] = []

  selectedDepartments: any[] = []
  selectedClasses: any[] = []
  selectedBatches: any[] = []


  resAcademicEntity: any = []
  academicEntity: any = []

  AcademicAccordion = {
    department_id: null,
    class_id: null
  }

  constructor(private scheduleHelperService: SchedulesHelperService, private fb: FormBuilder, private helperService: HelperServiceService, private scheduleApiService: SchedulesApiService, private commonStaffApiService: CommonStaffApiServiceService, private cascadedService: CascadingSelectServiceService,
    private toasterService: ToasterService
  ) {

    // Use computed signals to react to changes
    this.eventId = this.scheduleHelperService.eventId;

    this.actionType = this.scheduleHelperService.action;
    this.alltimeOptions = this.helperService.TimeOptions();
    this.courses$ = cascadedService.course$

    // Watch for changes in action type and selected event to populate form for editing
    effect(() => {
      const action = this.actionType();
      const pre = this.scheduleHelperService.preselectedRange();

      // If this is a create drawer opened from a calendar slot, prefill start/end date+time.
      if (action === 'create' && pre?.start && pre?.end && !this.scheduleHelperService.selectedEvent()) {
        const startDateStr = this.formatDateToYMD(pre.start);
        const startTimeStr = this.normalizeTimeToHHMM(pre.start.toTimeString().slice(0, 5));
        const endTimeStr = this.normalizeTimeToHHMM(pre.end.toTimeString().slice(0, 5));

        // Compute end dropdown options based on chosen start time.
        const startIndex = this.alltimeOptions.indexOf(startTimeStr);
        this.totimeOptions =
          startIndex >= 0 ? this.alltimeOptions.slice(startIndex + 1) : this.alltimeOptions;

        if (!this.totimeOptions.includes(endTimeStr)) {
          // fallback: keep end_time within available options, otherwise full list
          this.totimeOptions = this.alltimeOptions;
        }

        this.schedulerForm.get('scheduleTime')?.patchValue({
          start_date: startDateStr,
          start_time: startTimeStr,
        });
        this.schedulerForm.get('scheduleTime')?.get('end_time')?.setValue(endTimeStr);
        return;
      }

      const selectedEvent = this.scheduleHelperService.selectedEvent();

      const eventId = this.eventId();
      if (action !== 'edit') return;

      if (eventId) {
        const rawId = this.normalizeEventId(eventId);
        if (!rawId || rawId === this.lastLoadedEventId) return;
        this.lastLoadedEventId = rawId;
        this.scheduleApiService.getEventById(rawId).subscribe({
          next: (res: any) => {
            const payload =
              res?.status === 'success' ? res?.data : res?.data ?? res;
            if (payload) {
              this.scheduleHelperService.setSelectedEvent(payload);
              this.populateFormForEdit(payload);
            } else if (selectedEvent) {
              this.populateFormForEdit(selectedEvent);
            }
          },
          error: (err: any) => {
            console.error('Failed to fetch event details for edit:', err);
            if (selectedEvent) {
              this.populateFormForEdit(selectedEvent);
            }
          },
        });
        return;
      }

      if (selectedEvent) {
        this.populateFormForEdit(selectedEvent);
      }
    });
  } 


  ngOnInit(): void {
    this.schedulerForm = this.fb.group({
      summary: [''],
      description: ['', [Validators.maxLength(250)]],
      add_meet: ['false'], // Changed to string to match radio button values
      // add_meet_link is required only when session_type === 'external' AND add_meet === 'true'
      add_meet_link: ['', []],
      session_type: ['classroom'],
      // session_title is required only for external sessions
      session_title: [null, []],
      // course_id is required only for classroom sessions
      course_id: [null, []],
      attendees: this.fb.array([], [Validators.required, Validators.minLength(1)]),
      department_id: [[]],
      class_ids: [[]],
      batch_ids: [[]],
      additional_params: this.fb.array([], [Validators.required]),
      scheduleTime: this.fb.group({
        start_date: ['', [Validators.required, this.notPastDateValidator.bind(this)]],
        // end_date is only used when recurrence_type === 'daily'
        end_date: ['', [this.notBeforeEndDateValidator.bind(this)]],
        start_time: ['', [Validators.required]],
        end_time: ['', [Validators.required]],
        recurrence_type: [null],
        recurrence_days: [[]],
        recurrence_day_of_month: [''],
      })
    })

    // Date/time cross-field validation:
    // - If start_date is today, require start_time/end_time to be valid within today.
    // - If start_date is in the past, start_date validator handles it.
    this.schedulerForm.get('scheduleTime')?.valueChanges.subscribe(() => {
      this.applyTodayTimeValidation();
    });

    const scheduleTime = this.schedulerForm.get('scheduleTime') as FormGroup;
    scheduleTime.get('start_date')?.valueChanges.subscribe(() => {
      scheduleTime.get('end_date')?.updateValueAndValidity({ emitEvent: false });
    });
    scheduleTime.get('recurrence_type')?.valueChanges.subscribe(() => {
      scheduleTime.get('end_date')?.updateValueAndValidity({ emitEvent: false });
    });


    this.onUserSearch('')
    this.getAcademicEntities()

    this.isFormInitialized = true;
  }

  ngAfterViewInit() {
    console.log('ScheduleFormComponent ngAfterViewInit');
  }

  private populateFormForEdit(eventData: any) {

    if (!this.isFormInitialized || !this.schedulerForm) {
      console.log('Form not initialized yet, skipping population');
      return;
    }

    const startDateTime = this.parseApiDateTime(eventData.start);
    const endDateTime = this.parseApiDateTime(eventData.end);
    const startDateStr = startDateTime.date;
    const endDateStr = endDateTime.date;
    const startTimeStr = this.normalizeTimeToHHMM(startDateTime.time);
    const endTimeStr = this.normalizeTimeToHHMM(endDateTime.time);

  

    // Populate basic fields
    const meetLinkValue = eventData.meet_link || eventData.link || '';
    this.schedulerForm.patchValue({
      summary: eventData.summary || eventData.title || '',
      description: eventData.description || '',
      add_meet: meetLinkValue ? 'true' : 'false', // Changed to string
      add_meet_link: meetLinkValue,
      session_type: eventData.session_type || 'classroom',
      session_title: eventData.session_title || null,
      course_id: eventData.course_id || null,
    });

    // Set schedule time separately to avoid validation issues
    // First set start_time to update totimeOptions
    // Patch recurrence fields for edit (backend may return recurrence_type + recurrence_days)
    const recurrenceType = eventData?.recurrence_type ?? null;

    const recurrenceDays = Array.isArray(eventData?.recurrence_days)
      ? eventData.recurrence_days
      : String(eventData?.recurrence_days ?? '')
          .split(',')
          .map((day: string) => day.trim())
          .filter(Boolean);
    const recurrenceDayOfMonth = eventData?.recurrence_day_of_month ?? '';

    const recurrenceEndDate = eventData?.end_date ?? eventData?.end_date_by ?? eventData?.end_date_on ?? eventData?.end;

    // Ensure end_date is always YYYY-MM-DD so validator + payload are consistent.
    const recurrenceEndDateStr = recurrenceEndDate ? this.parseApiDateTime(recurrenceEndDate)?.date : '';

    this.schedulerForm.get('scheduleTime')?.patchValue({
      start_date: startDateStr,
      // If API sends date-only for end_date_by/end_date_on/end, ensure UI still gets YYYY-MM-DD.
      end_date: recurrenceEndDateStr || endDateStr,
      start_time: startTimeStr,
      recurrence_type: recurrenceType,
      recurrence_days: recurrenceType === 'weekly' ? recurrenceDays : [],
      recurrence_day_of_month: recurrenceType === 'monthly' ? recurrenceDayOfMonth : '',
    });


    // Update totimeOptions based on start_time.
    // IMPORTANT: When editing, we must not force the API end time to a 15-min slot.
    // So we compute the dropdown range from start_time but we still explicitly ensure API end_time exists below.
    const startIndex = this.alltimeOptions.indexOf(startTimeStr);
    this.totimeOptions = startIndex >= 0 ? this.alltimeOptions.slice(startIndex + 1) : this.alltimeOptions;
    console.log('computed totimeOptions:', { startIndex, totimeOptionsLength: this.totimeOptions.length, totimeOptionsStart: this.totimeOptions.slice(0, 5), totimeOptionsEnd: this.totimeOptions.slice(-5) });

    if (this.totimeOptions.length === 0) {
      console.log('totimeOptions empty after slice; falling back to full alltimeOptions');
      this.totimeOptions = this.alltimeOptions;
    }

    if (endTimeStr && !this.totimeOptions.includes(endTimeStr)) {
      if (this.alltimeOptions.includes(endTimeStr)) {
      this.totimeOptions = this.alltimeOptions.filter(t => t > startTimeStr);
        if (!this.totimeOptions.includes(endTimeStr)) {
       this.totimeOptions = this.alltimeOptions;
        }
      } else {
        this.totimeOptions = this.alltimeOptions;
      }
    }

    console.log('Setting end_time:', endTimeStr, 'totimeIncludesEnd:', this.totimeOptions.includes(endTimeStr), 'totime length:', this.totimeOptions.length);

    // Now set end_time (use a direct set to avoid any other change handlers overriding it)
    this.schedulerForm.get('scheduleTime')?.get('end_time')?.setValue(endTimeStr);




    // Handle attendees array and selectedUsers
    const attendeesArray = this.schedulerForm.get('attendees') as FormArray;
    attendeesArray.clear();
    this.selectedUsers = []; // Reset selected users
    if (eventData.attendee && Array.isArray(eventData.attendee)) {
      eventData.attendee.forEach((attendee: string) => {
        attendeesArray.push(this.fb.control(attendee));
        // Create user object for selectedUsers - try to find in users array or create basic object
        const userObj = this.users.find((u: any) => u.email === attendee) || { email: attendee, name: attendee };
        this.selectedUsers.push(userObj);
      });
    }

    // Handle additional parameters array
    const paramsArray = this.schedulerForm.get('additional_params') as FormArray;
    paramsArray.clear();
    if (eventData.additional_parameter && Array.isArray(eventData.additional_parameter)) {
      eventData.additional_parameter.forEach((param: string) => {
        paramsArray.push(this.fb.control(param));
      });
    }

    // Handle academic entities
    this.selectedDepartments = [];
    this.selectedClasses = [];
    this.selectedBatches = [];

    if (eventData.department_id && Array.isArray(eventData.department_id)) {
      this.selectedDepartments = eventData.department_id.map((dept: any) => dept.id);
    }

    if (eventData.class_id && Array.isArray(eventData.class_id)) {
      this.selectedClasses = eventData.class_id.map((cls: any) => cls.id);
    }

    if (eventData.batch_id && Array.isArray(eventData.batch_id)) {
      this.selectedBatches = eventData.batch_id.map((batch: any) => batch.id);
    }

    // Update form arrays
    this.schedulerForm.patchValue({
      department_id: this.selectedDepartments,
      class_ids: this.selectedClasses,
      batch_ids: this.selectedBatches
    });
  }


  getAcademicEntities() {
    this.cascadedService.academicEntries.subscribe({
      next: (res: any) => {
        this.academicEntity = res.data
        this.resAcademicEntity = res.data
      },
      error: (err: any) => {
        console.log(err);
      }
    })
  }



  scheduleEvent() {
    if (this.schedulerForm.invalid) {
      this.schedulerForm.markAllAsTouched();
      return;
    }
    const formValue = this.schedulerForm.getRawValue();
    const sessionType = formValue.session_type;
    const data = {
      summary: formValue.summary,
      description: formValue.description,
      date: formValue.scheduleTime.start_date,
      start_time: this.formatTimeForPayload(formValue.scheduleTime.start_time),
      end_time: this.formatTimeForPayload(formValue.scheduleTime.end_time),
      attendee: formValue.attendees,
      recurrence_days:
        formValue.scheduleTime.recurrence_type === 'weekly'
          ? Array.isArray(formValue.scheduleTime.recurrence_days)
            ? formValue.scheduleTime.recurrence_days.filter(Boolean)
            : String(formValue.scheduleTime.recurrence_days)
                .split(',')
                .map((d: string) => d.trim())
                .filter(Boolean)
          : [],
      recurrence_day_of_month:
        formValue.scheduleTime.recurrence_type === 'monthly'
          ? formValue.scheduleTime.recurrence_day_of_month
          : null,
      end_date:
        formValue.scheduleTime.recurrence_type === 'daily'
          ? formValue.scheduleTime.end_date
          : null,
      add_meet: formValue.add_meet === 'true' ? 1 : 0,

      // Session-type specific fields:
      webinar_link: sessionType === 'external' && formValue.add_meet === 'true' ? formValue.add_meet_link : null,
      session_title: sessionType === 'external' ? formValue.session_title : null,
      course_id: sessionType === 'classroom' ? formValue.course_id : null,

      session_type: sessionType,
      class_id: formValue.class_ids,
      batch_id: formValue.batch_ids,
      department_id: formValue.department_id,
      additional_parameter: formValue.additional_params,
      recurrence_type: formValue.scheduleTime.recurrence_type,
    };


    const action = this.actionType();
    const eventId = this.eventId();

    if (action === 'edit' && eventId) {
      // Update existing event
      this.scheduleApiService.updateEvent(eventId, data).subscribe({
        next: (res: any) => {
          if (res.status === 'success') {
            this.toasterService.setToaster('success', 'Success', res.message ?? 'Event updated successfully');
            this.scheduleHelperService.closeDrawer();
          }
        },
        error: (err: any) => {
          console.log(err);
          this.toasterService.setToaster('error', 'Error', 'Failed to update event');
        }
      });
    } else {
      // Create new event
      this.scheduleApiService.scheduleEvent(data).subscribe({
        next: (res: any) => {
          if (res.status === 'success') {
            this.toasterService.setToaster('success', 'Success', res.message ?? 'Event scheduled successfully');
            this.scheduleHelperService.closeDrawer();
          }
        },
        error: (err: any) => {
          console.log(err);
          this.toasterService.setToaster('error', 'Error', 'Failed to schedule event');
        }
      });
    }
  }


  onFromTimeChange(e: Event) {
    let value = (e.target as HTMLSelectElement).value;
    const startIndex = this.alltimeOptions.indexOf(value);
    this.totimeOptions = this.alltimeOptions.slice(startIndex + 1);

   const isEditing = this.actionType?.() === 'edit';
    if (!isEditing) {
      this.schedulerForm.get('scheduleTime')?.get('end_time')?.setValue(this.totimeOptions[0] || null);
    }
  }


  private formatTimeForPayload(time: string | null | undefined): string | null {
    if (!time) return null;
    const [hours = '00', minutes = '00'] = String(time).split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
  }

  private parseApiDateTime(value: string | Date | null | undefined): { date: string; time: string } {
    if (!value) {
      return { date: '', time: '' };
    }

    let rawValue = '';
    if (typeof value === 'string') {
      rawValue = value.trim();
    } else if (value instanceof Date) {
      rawValue = value.toISOString();
    } else {
      rawValue = String(value).trim();
    }

    // 1) Date + time (e.g. 2024-01-15T10:30:00 or 2024-01-15 10:30)
    const matchDateTime = rawValue.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{1,2}:\d{2})/);
    if (matchDateTime) {
      return { date: matchDateTime[1], time: this.normalizeTimeToHHMM(matchDateTime[2]) };
    }

    // 2) Date only (e.g. 2024-01-15)
    const matchDateOnly = rawValue.match(/^(\d{4}-\d{2}-\d{2})$/);
    if (matchDateOnly) {
      return { date: matchDateOnly[1], time: '' };
    }

    return { date: '', time: '' };
  }


  // Normalize API time into HH:MM so it can match the dropdown options
  private formatDateToYMD(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private normalizeTimeToHHMM(time: string | null | undefined): string {

    if (!time) return '';
    const str = String(time).trim();
    const match = str.match(/^(\d{1,2}):(\d{2})/);
    if (match) {
      const hh = match[1].padStart(2, '0');
      const mm = match[2];
      return `${hh}:${mm}`;
    }
    return str;
  }

  private notPastDateValidator(control: any) {
    const value: string = control?.value;
    if (!value) return null;

    // value format from input[type=date] is YYYY-MM-DD (local)
    const [y, m, d] = String(value).split('-').map((x: string) => Number(x));
    if (!y || !m || !d) return null;

    const selected = new Date(y, m - 1, d);
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return selected < todayOnly ? { pastDate: true } : null;
  }

  // For daily repeats, end_date must be >= start_date (payload field: date).
  private notBeforeEndDateValidator(control: any) {
    const value: string = control?.value;
    if (!value) return null;

    const scheduleTime = control?.parent as FormGroup | null;
    if (!scheduleTime || scheduleTime.get('recurrence_type')?.value !== 'daily' ) {
      return null;
    }

    const startDateValue: string = scheduleTime.get('start_date')?.value;
    if (!startDateValue) return null;

    // value format from input[type=date] is YYYY-MM-DD (local)
    const [y, m, d] = String(value).split('-').map((x: string) => Number(x));
    if (!y || !m || !d) return null;

    const selected = new Date(y, m - 1, d);

    const [minY, minM, minD] = String(startDateValue).split('-').map((x: string) => Number(x));
    if (!minY || !minM || !minD) return null;

    const minDateOnly = new Date(minY, minM - 1, minD);

    return selected < minDateOnly ? { endDateMin: true } : null;
  }


  private applyTodayTimeValidation() {
    const scheduleTime = this.schedulerForm.get('scheduleTime') as FormGroup;
    const startDateCtrl = scheduleTime.get('start_date');
    const startTimeCtrl = scheduleTime.get('start_time');
    const endTimeCtrl = scheduleTime.get('end_time');

    const startDateVal: string = startDateCtrl?.value;
    if (!startDateVal) return;

    // If start_date is in the past, only start_date error should show.
    if (startDateCtrl?.errors?.['pastDate']) {
      // Remove cross-field time errors to avoid confusing messages.
      const { pastDate: _pastDate1, ...rest } = startTimeCtrl?.errors ?? {};
      if (startTimeCtrl) startTimeCtrl.setErrors(Object.keys(rest).length ? rest : null);
      const { pastDate: _pastDate2, ...restEnd } = endTimeCtrl?.errors ?? {};
      if (endTimeCtrl) endTimeCtrl.setErrors(Object.keys(restEnd).length ? restEnd : null);

      return;
    }

    // Today check (local)
    const [y, m, d] = String(startDateVal).split('-').map((x: string) => Number(x));
    const selected = new Date(y, m - 1, d);
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const isToday = selected.getTime() === todayOnly.getTime();
    if (!isToday) {
      // Clear today-only errors when not today.
      if (startTimeCtrl) {
        const errs = { ...(startTimeCtrl.errors ?? {}) };
        delete errs['pastStartTime'];
        delete errs['invalidTimeRange'];
        startTimeCtrl.setErrors(Object.keys(errs).length ? errs : null);
      }
      if (endTimeCtrl) {
        const errs = { ...(endTimeCtrl.errors ?? {}) };
        delete errs['pastEndTime'];
        delete errs['invalidTimeRange'];
        endTimeCtrl.setErrors(Object.keys(errs).length ? errs : null);
      }
      return;
    }

    // Today: validate time against current time.
    const nowMinutes = today.getHours() * 60 + today.getMinutes();
    const parseHHMM = (t: string) => {
      const [hh, mm] = String(t).split(':').map((x: string) => Number(x));
      if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
      return hh * 60 + mm;
    };

    const startMinutes = startTimeCtrl?.value ? parseHHMM(startTimeCtrl.value) : null;
    const endMinutes = endTimeCtrl?.value ? parseHHMM(endTimeCtrl.value) : null;

    // Start time must not be in the past (for today)
    if (startMinutes === null) {
      return;
    }

    const shouldStartError = startMinutes < nowMinutes;
    if (startTimeCtrl) {
      const errs = { ...(startTimeCtrl.errors ?? {}) };
      if (shouldStartError) errs['pastStartTime'] = true;
      else delete errs['pastStartTime'];
      startTimeCtrl.setErrors(Object.keys(errs).length ? errs : null);
    }

    // End time must be after start time (also for today)
    if (endMinutes === null) return;
    const invalidRange = endMinutes <= startMinutes;
    if (startTimeCtrl) {
      const errs = { ...(startTimeCtrl.errors ?? {}) };
      if (invalidRange) errs['invalidTimeRange'] = true;
      else delete errs['invalidTimeRange'];
      startTimeCtrl.setErrors(Object.keys(errs).length ? errs : null);
    }
    if (endTimeCtrl) {
      const errs = { ...(endTimeCtrl.errors ?? {}) };
      if (invalidRange) errs['invalidTimeRange'] = true;
      else delete errs['invalidTimeRange'];
      endTimeCtrl.setErrors(Object.keys(errs).length ? errs : null);
    }
  }

  



  onRepeatChange(e: Event, day: string) {

    const isChecked = (e.target as HTMLInputElement).checked;
    const recurrenceDays = this.schedulerForm.get('scheduleTime')?.get('recurrence_days');
    const selectedDays = Array.isArray(recurrenceDays?.value) ? recurrenceDays?.value : [];

    if (isChecked) {
      recurrenceDays?.setValue(selectedDays.includes(day) ? selectedDays : [...selectedDays, day]);
    } else {
      recurrenceDays?.setValue(selectedDays.filter((item: string) => item !== day));
    }
  }

  isRepeatDaySelected(day: string): boolean {
    const selectedDays = this.schedulerForm.get('scheduleTime.recurrence_days')?.value;
    return Array.isArray(selectedDays) && selectedDays.includes(day);
  }

  onUserSearch = debounce((e?: any) => {
    const str = e?.target?.value || '';

    // Fetch all teachers in one call using per_page=all (no pagination loop)
    this.commonStaffApiService.searchAllTeachers(str, 'TCH').subscribe({
      next: (res: any) => {
        const data = res?.data ?? [];
        // remove duplicates by email (safety)
        const deduped = data.filter((u: any, idx: number, arr: any[]) => {
          const email = u?.email ?? u?.email_id;
          return email ? arr.findIndex(x => (x?.email ?? x?.email_id) === email) === idx : true;
        });
        this.users = deduped;
      },
      error: () => {
        this.users = [];
      }
    });
    return;
  }, 500);




  onSelectUser(i: any) {
    this.selectedUsers.push(i)
    const users = this.schedulerForm.get('attendees') as FormArray
    users.push(this.fb.control(i.email))
  }
  removeUser(i: any) {
    this.selectedUsers.splice(i, 1)
    const users = this.schedulerForm.get('attendees') as FormArray
    users.removeAt(i)
  }

  toggleAcademicAccordion(id: any, type: 'department' | 'class') {
    if (type === 'department') {
      this.AcademicAccordion.department_id = this.AcademicAccordion.department_id === id ? null : id;
      this.AcademicAccordion.class_id = null;
    } else if (type === 'class') {
      this.AcademicAccordion.class_id = this.AcademicAccordion.class_id === id ? null : id;
    }
  }


  get AdditionalParamsArray(): FormArray {
    return this.schedulerForm.get('additional_params') as FormArray;
  }

  isUserSelected(email: any) {
    return this.selectedUsers.some((i: any) => i.email === email)
  }




  searchAcademicEntity = debounce((e: Event) => {
    const query = (e.target as HTMLInputElement).value;
    if (!query) {
      this.academicEntity = this.resAcademicEntity;
      return;
    }
    this.academicEntity = this.helperService.searchHierarchy(this.resAcademicEntity, query);
  }, 500);



  toggleSelection(type: 'department' | 'class' | 'batch', dep: any, cls?: any, batch?: any, event?: Event) {
    const checked = (event?.target as HTMLInputElement).checked;

    if (type === 'department') {
      this.setDepartment(dep.id, checked);
      (dep.classes || []).forEach((c: any) => {
        this.setClass(c.id, checked);
        (c.batches || []).forEach((b: any) => this.setBatch(b.id, checked));
      });
    }

    if (type === 'class' && cls) {
      this.setClass(cls.id, checked);

      if (checked) {
        this.AcademicAccordion.department_id = dep.id;
        this.AcademicAccordion.class_id = cls.id;
        (cls.batches || []).forEach((batch: any) => this.setBatch(batch.id, true));
      } else {
        this.clearClassBatches(cls);
      }

      this.setDepartment(dep.id, this.hasSelectedClass(dep));
    }

    if (type === 'batch' && cls && batch) {
      this.setBatch(batch.id, checked);
    }
    this.schedulerForm.get('department_id')?.setValue(this.selectedDepartments || []);
    this.schedulerForm.get('class_ids')?.setValue(this.selectedClasses || []);
    this.schedulerForm.get('batch_ids')?.setValue(this.selectedBatches || []);
    
  }

  setDepartment(id: number, checked: boolean) {
    if (checked && !this.selectedDepartments.includes(id)) {
      this.selectedDepartments.push(id);
    } else if (!checked) {
      this.selectedDepartments = this.selectedDepartments.filter(d => d !== id);
    }
  }

  setClass(id: number, checked: boolean) {
    if (checked && !this.selectedClasses.includes(id)) {
      this.selectedClasses.push(id);
    } else if (!checked) {
      this.selectedClasses = this.selectedClasses.filter(c => c !== id);
    }
  }

  setBatch(id: number, checked: boolean) {
    if (checked && !this.selectedBatches.includes(id)) {
      this.selectedBatches.push(id);
    } else if (!checked) {
      this.selectedBatches = this.selectedBatches.filter(b => b !== id);
    }
  }

  clearClassBatches(cls: any) {
    const batchIds = (cls.batches || []).map((batch: any) => batch.id);
    this.selectedBatches = this.selectedBatches.filter(batchId => !batchIds.includes(batchId));
  }

  isDepartmentSelected(dep: any): boolean {
    return this.selectedDepartments.includes(dep.id);
  }

  isClassSelected(cls: any): boolean {
    return this.selectedClasses.includes(cls.id);
  }

  isBatchSelected(batch: any): boolean {
    return this.selectedBatches.includes(batch.id);
  }

  hasSelectedClass(dep: any): boolean {
    return (dep.classes || []).some((cls: any) => this.isClassSelected(cls));
  }

  get selectedAcademicPreview(): { type: 'department' | 'class' | 'batch'; label: string; parent?: string }[] {
    const preview: { type: 'department' | 'class' | 'batch'; label: string; parent?: string }[] = [];

    const selectedDepartmentIds = new Set(this.selectedDepartments);
    const selectedClassIds = new Set(this.selectedClasses);
    const selectedBatchIds = new Set(this.selectedBatches);

    (this.resAcademicEntity || []).forEach((department: any) => {
      if (selectedDepartmentIds.has(department.id)) {
        preview.push({
          type: 'department',
          label: department.department_name
        });
      }

      (department.classes || []).forEach((cls: any) => {
        const selectedBatches = (cls.batches || []).filter((batch: any) => selectedBatchIds.has(batch.id));

        if (selectedClassIds.has(cls.id) && selectedBatches.length === 0) {
          preview.push({
            type: 'class',
            label: cls.class_name,
            parent: department.department_name
          });
        }

        selectedBatches.forEach((batch: any) => {
          preview.push({
            type: 'batch',
            label: batch.batch_name,
            parent: cls.class_name
          });
        });
      });
    });

    return preview;
  }


  updateAdditionalParamsList(action: 'add' | 'remove', params?: string, index?: number) {
    const formParameters = this.schedulerForm.get('additional_params') as FormArray
    if (action === 'add' && params) {
      formParameters.push(this.fb.control(params))
      this.additionalParamsInput.nativeElement.value = '';
    } else if (action === 'remove' && index !== undefined) {
      formParameters.removeAt(index)
    }
  }

  private normalizeEventId(value: any): string | number | null {
    if (value === undefined || value === null || value === '') return null;
    const rawString = String(value);
    if (rawString.startsWith('evt-')) {
      return rawString.replace(/^evt-/, '').split('-')[0];
    }
    return rawString.split('-')[0];
  }
}
